import { useState } from 'react'
import { registro, mensagemDoErro } from '../services/api'
import { Card, Titulo, Botao, Provedor, Etiqueta, Erro, Vazio } from '../components/ui'

/**
 * O registro conferido contra o catálogo ao vivo de cada provedor.
 *
 * Duas leituras, e a ordem na tela é deliberada: o que SUMIU vem primeiro,
 * porque é o que quebra produto. O que apareceu de novo é curiosidade útil; o
 * que sumiu é um id configurado que o provedor não serve mais — e ninguém
 * avisa. O VanguardAI só descobriu porque alguém foi conferir, e até lá o tier
 * pago devolvia 502.
 *
 * Sob demanda, nunca automático: perguntar gasta chave e cota, e um alerta
 * diário que quase sempre diz "tudo certo" é um alerta que ninguém lê.
 */

const ROTULO_DO_STATUS = {
  ok: null,
  sem_chave: ['aviso', 'sem chave configurada'],
  nao_consultavel: ['neutro', 'só catálogo — não é consultável'],
  erro: ['perigo', 'não respondeu'],
}

export default function Catalogo() {
  const [dados, setDados] = useState(null)
  const [erro, setErro] = useState('')
  const [indo, setIndo] = useState(false)

  async function conferir() {
    setErro(''); setIndo(true)
    try {
      setDados(await registro.catalogo())
    } catch (e) { setErro(mensagemDoErro(e)) } finally { setIndo(false) }
  }

  const sumidos = (dados?.providers ?? []).flatMap((p) =>
    p.sumiram.map((m) => ({ provider: p.provider, model: m })))

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <Titulo sub="Pergunta a cada provedor quais modelos ele serve hoje e compara com o registro publicado. Não muda nada — só relata.">
          Catálogo dos provedores
        </Titulo>
        <Botao onClick={conferir} disabled={indo}>
          {indo ? 'perguntando…' : 'Conferir agora'}
        </Botao>
      </div>

      <Erro>{erro}</Erro>

      {!dados && !erro && (
        <Card>
          <Vazio>
            Nada conferido ainda. A conferência é sob demanda porque gasta chave e cota —
            e porque um alerta diário que quase sempre diz “tudo certo” vira um alerta que
            ninguém lê.
          </Vazio>
        </Card>
      )}

      {dados && sumidos.length > 0 && (
        <Card className="border-red-300 bg-red-50/50 p-5">
          <h3 className="mb-2 text-sm font-bold uppercase tracking-wide text-red-800">
            Declarado, e o provedor não serve mais
          </h3>
          <p className="mb-3 max-w-[74ch] text-sm text-zinc-700">
            Um id configurado que sumiu do catálogo devolve erro na primeira chamada de verdade,
            e nenhum provedor avisa quando isso acontece.
          </p>
          <ul className="flex flex-col gap-1">
            {sumidos.map((s) => (
              <li key={`${s.provider}/${s.model}`} className="font-mono text-xs text-red-900">
                {s.model} <span className="text-red-400">· {s.provider}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {dados && (
        <div className="flex flex-col gap-3">
          {dados.providers.map((p) => {
            const marca = ROTULO_DO_STATUS[p.status]
            return (
              <Card key={p.provider} className="p-4">
                <div className="flex flex-wrap items-center gap-3">
                  <Provedor id={p.provider} />
                  {marca ? (
                    <Etiqueta tom={marca[0]}>{marca[1]}</Etiqueta>
                  ) : (
                    <span className="font-mono text-xs text-zinc-500">
                      {p.totalNoProvedor} modelos no catálogo
                    </span>
                  )}
                  {p.erro && <span className="font-mono text-[11px] text-red-600">{p.erro}</span>}
                  {p.status === 'ok' && p.sumiram.length === 0 && (
                    <Etiqueta tom="ok">registro em dia</Etiqueta>
                  )}
                </div>

                {p.novos.length > 0 && (
                  <div className="mt-3">
                    <h4 className="mb-1.5 text-[11px] font-bold uppercase tracking-wide text-zinc-400">
                      No provedor e fora do registro
                    </h4>
                    <div className="flex flex-wrap gap-1">
                      {p.novos.map((m) => (
                        <span
                          key={m}
                          className="rounded border border-zinc-200 bg-zinc-50 px-1.5 py-0.5 font-mono text-[11px] text-zinc-600"
                        >
                          {m}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </Card>
            )
          })}
        </div>
      )}

      {dados && (
        <p className="max-w-[74ch] text-xs leading-relaxed text-zinc-500">
          <b>O que esta tela não faz:</b> dizer qual modelo é melhor. Catálogo de provedor não
          traz nenhuma medição, e afirmar isso a partir de um nome de versão seria chute. Melhor se
          decide medindo contra os seus próprios dados — foi assim que o{' '}
          <span className="font-mono">gpt-oss-20b</span> ganhou do 120b no estúdio: mesma acurácia
          em 16 nichos reais, metade do preço, mediana de 370ms contra 491ms.
        </p>
      )}
    </div>
  )
}
